/**
 * Header Component
 * =================
 * Top header bar with user info and actions
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, usePermissions, useOrg } from '../../hooks';
import { Badge } from '../ui';
import OrgSwitcher from './OrgSwitcher';
import OrgImpersonationBanner from './OrgImpersonationBanner';

const Header = ({ onMobileMenuOpen }) => {
  const { user, logout } = useAuth();
  const { roles, isOrgAdmin } = usePermissions();
  const { activeOrg } = useOrg();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Always return to the home page, not the org-specific login
    window.location.href = '/';
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  return (
    <header className="bg-white dark:bg-[#242628] sticky top-0 z-30">
      <OrgImpersonationBanner />
      <div className="px-6 h-[65px] flex items-center border-b border-[#F0F0F0] dark:border-[#3A3C3E]">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-2">
            {/* Hamburger — opens the sidebar drawer on mobile */}
            <button
              onClick={onMobileMenuOpen}
              type="button"
              className="md:hidden flex items-center justify-center w-9 h-9 -ml-2 rounded-lg text-[#373D3F] dark:text-[#E1E1E1] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] transition-colors"
              aria-label="Open navigation menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <OrgSwitcher />
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4 ml-auto">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(prev => !prev)}
              className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] transition-colors"
            >
              {/* User Avatar */}
              <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>

              {/* User Name */}
              <span className="text-sm font-medium text-[#373D3F] dark:text-[#E1E1E1] hidden md:block">
                {user?.first_name} {user?.last_name}
              </span>

              {/* Dropdown Icon */}
              <svg
                className={`w-5 h-5 text-[#90A4AE] dark:text-[#6E7278] transition-transform ${
                  showDropdown ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#242628] rounded-lg shadow-lg border border-[#E0E0E0] dark:border-[#3A3C3E] py-2 animate-dropdown-in origin-top-right">
                {/* User Roles */}
                {/* <div className="px-4 py-3 border-b border-[#E0E0E0] dark:border-[#3A3C3E]">
                  <p className="text-xs font-medium text-[#90A4AE] dark:text-[#6E7278] mb-2">Your Roles</p>
                  <div className="flex flex-wrap gap-1">
                    {roles.map((role) => (
                      <Badge key={role} variant="accent" size="sm">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div> */}

                {/* Menu Items */}
                <div className="py-1">
                  <Link
                    to="/profile"
                    className="block px-4 py-2.5 text-sm text-[#646A71] dark:text-[#A0A4A8] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] hover:text-primary"
                    onClick={() => setShowDropdown(false)}
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2.5 text-sm text-[#646A71] dark:text-[#A0A4A8] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] hover:text-primary"
                    onClick={() => setShowDropdown(false)}
                  >
                    Settings
                  </Link>
                  {isOrgAdmin && activeOrg?.id && (
                    <Link
                      to={`/organizations/${activeOrg.id}`}
                      className="block px-4 py-2.5 text-sm text-[#646A71] dark:text-[#A0A4A8] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] hover:text-primary"
                      onClick={() => setShowDropdown(false)}
                    >
                      Organization Settings
                    </Link>
                  )}
                  <Link
                    to="/profile#integrations"
                    className="block px-4 py-2.5 text-sm text-[#646A71] dark:text-[#A0A4A8] hover:bg-[#F5F3EF] dark:hover:bg-[#2E3033] hover:text-primary"
                    onClick={() => setShowDropdown(false)}
                  >
                    Integrations
                  </Link>
                </div>

                {/* Logout */}
                <div className="border-t border-[#E0E0E0] dark:border-[#3A3C3E] pt-1">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
